/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import type {PriceIds} from '@app/actions/PremiumActionCreators';
import * as PremiumActionCreators from '@app/actions/PremiumActionCreators';
import * as ToastActionCreators from '@app/actions/ToastActionCreators';
import {Logger} from '@app/lib/Logger';
import * as LocaleUtils from '@app/utils/LocaleUtils';
import {formatMinorUnitPrice, type PricingMode} from '@app/utils/PricingUtils';
import {useLingui} from '@lingui/react/macro';
import {useEffect, useMemo, useState} from 'react';

const logger = new Logger('usePremiumData');
const PRICE_PLACEHOLDER = '...';

export interface PremiumData {
	priceIds: PriceIds | null;
	monthlyPrice: string;
	yearlyPrice: string;
	giftMonthlyPrice: string;
	giftYearlyPrice: string;
	localizedPriceIds: PriceIds | null;
	basePriceIds: PriceIds | null;
	hasPricingChoice: boolean;
	selectedPricingMode: PricingMode;
	localizedCurrency: string | null;
	baseCurrency: string | null;
	localizedGiftSelfRedeemOnly: boolean;
}

export const usePremiumData = (
	countryCode: string | null,
	pricingMode: PricingMode = 'localized',
	{showErrorToast = false}: {showErrorToast?: boolean} = {},
): PremiumData => {
	const {t} = useLingui();
	const [localizedPriceIds, setLocalizedPriceIds] = useState<PriceIds | null>(null);
	const [basePriceIds, setBasePriceIds] = useState<PriceIds | null>(null);
	const locale = LocaleUtils.getCurrentLocale();

	useEffect(() => {
		let mounted = true;
		const fetchData = async () => {
			try {
				const prices = await PremiumActionCreators.fetchPriceIds(countryCode ?? undefined, 'localized');
				if (!mounted) return;
				setLocalizedPriceIds(prices);
			} catch (error) {
				logger.error('Failed to fetch premium data', error);
				if (!mounted) return;
				if (showErrorToast) {
					ToastActionCreators.error(t`Failed to load premium information. Please try again later.`);
				}
			}
		};
		fetchData();
		return () => {
			mounted = false;
		};
	}, [countryCode, showErrorToast, t]);

	useEffect(() => {
		let mounted = true;
		const fetchData = async () => {
			try {
				const prices = await PremiumActionCreators.fetchPriceIds(countryCode ?? undefined, 'base');
				if (!mounted) return;
				setBasePriceIds(prices);
			} catch (error) {
				logger.warn('Failed to fetch base premium data', error);
				if (!mounted) return;
				if (showErrorToast && pricingMode === 'base') {
					ToastActionCreators.error(t`Failed to load standard USD/EUR pricing. Please try again later.`);
				}
			}
		};
		fetchData();
		return () => {
			mounted = false;
		};
	}, [countryCode, pricingMode, showErrorToast, t]);

	const hasPricingChoice =
		typeof localizedPriceIds?.currency === 'string' &&
		typeof basePriceIds?.currency === 'string' &&
		localizedPriceIds.currency !== basePriceIds.currency;

	const priceIds = pricingMode === 'base' && hasPricingChoice ? basePriceIds : localizedPriceIds;

	const monthlyPrice = useMemo(() => {
		return (
			formatMinorUnitPrice(priceIds?.monthly_amount_minor, priceIds?.currency, locale) ??
			PRICE_PLACEHOLDER
		);
	}, [locale, priceIds?.currency, priceIds?.monthly_amount_minor]);

	const yearlyPrice = useMemo(() => {
		return (
			formatMinorUnitPrice(priceIds?.yearly_amount_minor, priceIds?.currency, locale) ??
			PRICE_PLACEHOLDER
		);
	}, [locale, priceIds?.currency, priceIds?.yearly_amount_minor]);

	const giftMonthlyPrice = useMemo(() => {
		return (
			formatMinorUnitPrice(priceIds?.gift_1_month_amount_minor, priceIds?.gift_currency, locale) ??
			PRICE_PLACEHOLDER
		);
	}, [locale, priceIds?.gift_1_month_amount_minor, priceIds?.gift_currency]);

	const giftYearlyPrice = useMemo(() => {
		return (
			formatMinorUnitPrice(priceIds?.gift_1_year_amount_minor, priceIds?.gift_currency, locale) ??
			PRICE_PLACEHOLDER
		);
	}, [locale, priceIds?.gift_1_year_amount_minor, priceIds?.gift_currency]);

	return {
		priceIds,
		monthlyPrice,
		yearlyPrice,
		giftMonthlyPrice,
		giftYearlyPrice,
		localizedPriceIds,
		basePriceIds,
		hasPricingChoice,
		selectedPricingMode: pricingMode,
		localizedCurrency: localizedPriceIds?.currency ?? null,
		baseCurrency: hasPricingChoice ? (basePriceIds?.currency ?? null) : null,
		localizedGiftSelfRedeemOnly:
			typeof localizedPriceIds?.gift_currency === 'string' &&
			localizedPriceIds.gift_currency !== 'USD' &&
			localizedPriceIds.gift_currency !== 'EUR',
	};
};
